import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Cart drawer with one explicit vertical scroller.
 * The base DialogComponent freezes body with position:fixed. That is useful for
 * generic dialogs, but on mobile Safari it can interfere with a long nested
 * overflow area after the cart is morphed. The cart drawer therefore swaps that
 * lock for a root overflow lock while it is open.
 */
class CartDrawerComponent extends DialogComponent {
  #pageScrollY = 0;
  #stabilizeRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    cancelAnimationFrame(this.#stabilizeRaf);
    this.#clearCartScrollLock();
  }

  showDialog() {
    this.#pageScrollY = window.scrollY;
    super.showDialog();
  }

  #handleCartUpdate = (event) => {
    const source = event?.detail?.data?.source || '';
    const popupOnlySources = new Set([
      'product-form-component',
      'vehicle-customizer',
      'ao-shirt-customizer',
      'ao-native-upload',
      'ao-native-return',
    ]);
    if (source !== 'ao-cart-benefits' && !popupOnlySources.has(source) && this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    const { dialog } = this.refs;
    if (!dialog?.open) return;

    // Cart events are dispatched just before/while Shopify morphs the header
    // section. Re-check the scroll box on the next two frames, after layout.
    this.#scheduleScrollerStabilize();
  };

  #handleDialogOpen = () => {
    document.documentElement.classList.add('ao-cart-drawer-open');

    // DialogComponent has already captured the page position. Replace its
    // body-position lock with a root overflow lock for this drawer only.
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#pageScrollY, behavior: 'instant' });

    this.#scheduleScrollerStabilize();
  };

  #handleDialogClose = () => {
    this.#clearCartScrollLock();
  };

  #clearCartScrollLock() {
    document.documentElement.classList.remove('ao-cart-drawer-open');
  }

  #scheduleScrollerStabilize() {
    cancelAnimationFrame(this.#stabilizeRaf);
    this.#stabilizeRaf = requestAnimationFrame(() => {
      this.#stabilizeRaf = requestAnimationFrame(() => {
        const content = this.refs.dialog?.querySelector('[data-cart-drawer-scroll]');
        if (!(content instanceof HTMLElement)) return;

        // Reading these values forces the freshly morphed layout to settle.
        const maxScrollTop = Math.max(0, content.scrollHeight - content.clientHeight);
        if (content.scrollTop > maxScrollTop) content.scrollTop = maxScrollTop;
      });
    });
  }

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
